/**
 * Milestone Rules — generic per-rule milestone email admin.
 *
 * Users create arbitrary rules with one of two trigger types:
 *   - nth_visit: fire after the client's Nth non-cancelled booking of any
 *                of the selected appointment types (optionally with a
 *                delay of `min_hours_after_visit`)
 *   - days_after_appointment: fire N days after the client's earliest
 *                booking of any of the selected appointment types
 *
 * Rules are stored server-side; the daily cron runs every enabled rule
 * once a day. Every send is deduped per (rule, client).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Flag, Plus, Trash2, Save, Send, Play, Pencil, Eye } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

const MERGE_HINTS = [
  "{first_name}", "{name}", "{count}", "{threshold}",
  "{anchor_date}", "{anchor_type}", "{trigger_date}", "{occurrence}",
];

const EMPTY_RULE = {
  name: "",
  enabled: false,
  trigger_type: "nth_visit",
  appointment_type_ids: [],
  n: 1,
  days_after: 0,
  min_hours_after_visit: 0,
  recurring: false,
  require_recent_visit_within_days: 0,
  send_email: true,
  subject: "",
  html_body: "",
  sender_email: "",
  send_sms: false,
  sms_body: "",
};

export default function MilestoneRules() {
  const [rules, setRules] = useState([]);
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);   // rule being edited, or "new"
  const [previewTo, setPreviewTo] = useState("");
  const [previewPhone, setPreviewPhone] = useState("");
  const [running, setRunning] = useState(null);   // rule_id or null
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get("/milestones/rules");
      setRules(r.data?.rows || []);
    } catch (e) {
      toast.error("Could not load rules", { description: e?.response?.data?.detail || e.message });
    }
    try {
      const t = await api.get("/appointment-types");
      setTypes(t.data || []);
    } catch { setTypes([]); }
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function saveRule(rule) {
    try {
      if (rule.id) {
        await api.put(`/milestones/rules/${rule.id}`, rule);
      } else {
        await api.post("/milestones/rules", rule);
      }
      toast.success("Rule saved");
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function deleteRule(id) {
    if (!confirm("Delete this rule? Its sent-history dedup rows will also be dropped.")) return;
    try {
      await api.delete(`/milestones/rules/${id}`);
      toast.success("Rule deleted");
      await refresh();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function toggleEnabled(rule) {
    await saveRule({ ...rule, enabled: !rule.enabled });
  }

  async function preview(rule) {
    if (!previewTo.trim()) {
      toast.error("Enter a preview email above first");
      return;
    }
    try {
      const body = { rule_id: rule.id, to_email: previewTo.trim() };
      if (rule.send_sms && previewPhone.trim()) body.to_phone = previewPhone.trim();
      const r = await api.post("/milestones/preview", body);
      const bits = [];
      if (r.data?.ok) bits.push(`Email → ${previewTo}`);
      else bits.push("Email failed");
      if (r.data?.sms) {
        bits.push(r.data.sms.sent ? `SMS → ${r.data.sms.to}` : `SMS: ${r.data.sms.status || "failed"}`);
      }
      toast.success("Preview dispatched", { description: bits.join(" · ") });
    } catch (e) {
      toast.error("Preview failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function runRule(rule) {
    setRunning(rule.id);
    try {
      const r = await api.post(`/milestones/run-now?rule_id=${rule.id}`);
      const res = (r.data?.results || [])[0] || {};
      toast.success(`"${rule.name}" ran`, {
        description: `Sent ${res.sent || 0} · Matched ${res.clients_matched || 0} clients`,
      });
      await refresh();
    } catch (e) {
      toast.error("Run failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(null);
    }
  }

  function typeName(id) {
    return types.find((t) => Number(t.id) === Number(id))?.name || `#${id}`;
  }

  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none" data-testid="milestone-rules">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <Flag className="h-3.5 w-3.5" /> Milestone Emails
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY_RULE })}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="milestone-add-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New rule
        </Button>
      </div>
      <p className="mt-2 text-sm text-zinc-600 max-w-3xl">
        Send automated emails on client milestones. Two trigger types cover most cases:
        <b> Nth visit</b> (e.g. post-first-visit feedback, 12-class GLA:D completion) or
        <b> N days after appointment</b> (e.g. 3-month follow-up). Each rule can enable/disable
        independently. Runs daily at 07:20 local time.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Label className="text-2xs uppercase tracking-wider text-zinc-500 whitespace-nowrap">Preview to</Label>
        <Input value={previewTo} onChange={(e) => setPreviewTo(e.target.value)}
          placeholder="you@example.com"
          className="border-zinc-300 font-mono-num max-w-xs" data-testid="milestone-preview-email" />
        <Input value={previewPhone} onChange={(e) => setPreviewPhone(e.target.value)}
          placeholder="+61… (only for SMS rules)"
          className="border-zinc-300 font-mono-num max-w-xs" data-testid="milestone-preview-phone" />
      </div>

      {/* Rules list */}
      <div className="mt-5 space-y-2" data-testid="milestone-rules-list">
        {rules.length === 0 && !loading && (
          <div className="p-6 text-center text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md">
            No rules yet. Click <b>New rule</b> to create one.
          </div>
        )}
        {rules.map((r) => (
          <div key={r.id} className="p-3 rounded-md border border-zinc-200 flex items-center gap-3" data-testid={`milestone-row-${r.id}`}>
            <Switch
              checked={r.enabled}
              onCheckedChange={() => toggleEnabled(r)}
              data-testid={`milestone-toggle-${r.id}`}
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-zinc-900 flex items-center gap-2 flex-wrap">
                {r.name}
                <Badge variant="outline" className="border-zinc-300 text-zinc-600 text-2xs">
                  {r.trigger_type === "nth_visit"
                    ? `visit #${r.n}${r.min_hours_after_visit ? ` + ${r.min_hours_after_visit}h` : ""}`
                    : `${r.days_after}d after${r.recurring ? " · recurring" : ""}`}
                </Badge>
                {r.require_recent_visit_within_days ? (
                  <Badge variant="outline" className="border-amber-300 text-amber-700 text-2xs">
                    active-only ({r.require_recent_visit_within_days}d)
                  </Badge>
                ) : null}
                <Badge variant="outline" className="border-blue-300 text-blue-700 text-2xs">
                  {r.send_email !== false && r.send_sms ? "Email + SMS"
                    : r.send_email === false && r.send_sms ? "SMS only"
                    : "Email only"}
                </Badge>
              </div>
              <div className="text-2xs text-zinc-500 truncate">
                {(r.appointment_type_ids || []).map(typeName).join(" · ") || "no types"}
                {" · "}<span className="font-mono-num">{r.subject || "(no subject)"}</span>
              </div>
            </div>
            <Button size="sm" variant="outline" className="border-zinc-300" onClick={() => preview(r)} data-testid={`milestone-preview-${r.id}`}>
              <Send className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" className="border-zinc-300" onClick={() => runRule(r)} disabled={running === r.id} data-testid={`milestone-run-${r.id}`}>
              <Play className={"h-3.5 w-3.5 " + (running === r.id ? "animate-spin" : "")} />
            </Button>
            <Button size="sm" variant="outline" className="border-zinc-300" onClick={() => setEditing(r)} data-testid={`milestone-edit-${r.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => deleteRule(r.id)} data-testid={`milestone-delete-${r.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>

      {editing && (
        <RuleEditor
          rule={editing}
          types={types}
          onClose={() => setEditing(null)}
          onSave={saveRule}
        />
      )}
    </Card>
  );
}

// ============================================================================
// Editor dialog
// ============================================================================
function RuleEditor({ rule, types, onClose, onSave }) {
  const [form, setForm] = useState(rule);
  const [showPreview, setShowPreview] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const selectedIds = useMemo(() => new Set((form.appointment_type_ids || []).map((x) => Number(x))), [form.appointment_type_ids]);
  function toggleType(id) {
    const next = new Set(selectedIds);
    if (next.has(Number(id))) next.delete(Number(id));
    else next.add(Number(id));
    set({ appointment_type_ids: Array.from(next) });
  }

  function save() {
    if (!form.name.trim()) return toast.error("Rule name is required");
    if ((form.appointment_type_ids || []).length === 0) return toast.error("Pick at least one appointment type");
    if (!form.send_email && !form.send_sms) return toast.error("Turn on Email or SMS (or both)");
    if (form.send_email && (!form.subject.trim() || !form.html_body.trim())) {
      return toast.error("Subject and email body are required when 'Send email' is on");
    }
    if (form.send_sms && !(form.sms_body || "").trim()) return toast.error("SMS body is required when 'Also send SMS' is on");
    onSave({
      ...form,
      n: Number(form.n) || 1,
      days_after: Number(form.days_after) || 0,
      min_hours_after_visit: Number(form.min_hours_after_visit) || 0,
      recurring: !!form.recurring && form.trigger_type === "days_after_appointment",
      require_recent_visit_within_days: Math.max(0, Number(form.require_recent_visit_within_days) || 0),
    });
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="milestone-editor">
        <DialogHeader>
          <DialogTitle>{form.id ? "Edit milestone rule" : "New milestone rule"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Rule name</Label>
              <Input value={form.name} onChange={(e) => set({ name: e.target.value })}
                placeholder="Post-first-visit feedback"
                className="border-zinc-300 mt-1.5" data-testid="rule-name" />
            </div>
            <label className="flex items-center gap-2 mt-6 md:mt-8 text-sm cursor-pointer">
              <Switch checked={form.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="rule-enabled" />
              Enabled
            </label>
          </div>

          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Trigger</Label>
            <Select value={form.trigger_type} onValueChange={(v) => set({ trigger_type: v })}>
              <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="rule-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nth_visit">Nth visit — fire after the client's Nth non-cancelled booking</SelectItem>
                <SelectItem value="days_after_appointment">N days after appointment — fire N days after the earliest booking</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.trigger_type === "nth_visit" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Visit number (N)</Label>
                <Input type="number" min={1} value={form.n} onChange={(e) => set({ n: e.target.value })}
                  className="border-zinc-300 mt-1.5 font-mono-num max-w-[120px]" data-testid="rule-n" />
                <p className="mt-1 text-2xs text-zinc-500">1 = post-first-visit · 12 = GLA:D completion</p>
              </div>
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Min hours after that visit</Label>
                <Input type="number" min={0} value={form.min_hours_after_visit}
                  onChange={(e) => set({ min_hours_after_visit: e.target.value })}
                  className="border-zinc-300 mt-1.5 font-mono-num max-w-[120px]" data-testid="rule-delay" />
                <p className="mt-1 text-2xs text-zinc-500">0 = send as soon as daily tick sees it. 24 = wait a day after the visit.</p>
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Days after earliest matching appointment</Label>
              <Input type="number" value={form.days_after} onChange={(e) => set({ days_after: e.target.value })}
                className="border-zinc-300 mt-1.5 font-mono-num max-w-[120px]" data-testid="rule-days-after" />
              <p className="mt-1 text-2xs text-zinc-500">
                83 = GLA:D 3-month anniversary minus 7 days (90 − 7).
                365 = 12 months after Initial Assessment (pair with the &quot;still attending&quot; gate below).
                Any positive number is supported; the send happens once we cross that day, with a 30-day catch-up window.
              </p>
              <label className="flex items-start gap-2 mt-3 text-sm cursor-pointer">
                <Switch
                  checked={!!form.recurring}
                  onCheckedChange={(v) => set({ recurring: v })}
                  data-testid="rule-recurring"
                />
                <span>
                  <span className="font-medium">Recurring anniversary</span>
                  <span className="block text-2xs text-zinc-500 mt-0.5">
                    When on, this rule fires every {form.days_after || "N"} days (year 1, year 2, year 3…).
                    Perfect for annual check-ins. When off, it fires exactly once per client.
                  </span>
                </span>
              </label>
            </div>
          )}

          <div className="p-3 border border-zinc-200 rounded-md bg-zinc-50/60">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Still-attending gate (optional)
            </Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input
                type="number" min={0}
                value={form.require_recent_visit_within_days}
                onChange={(e) => set({ require_recent_visit_within_days: e.target.value })}
                className="border-zinc-300 font-mono-num max-w-[120px]"
                data-testid="rule-recent-visit-days"
              />
              <span className="text-xs text-zinc-600">
                days — client must have any non-cancelled appointment within this window before the trigger fires
              </span>
            </div>
            <p className="mt-1.5 text-2xs text-zinc-500">
              0 = disabled (default). Set to <span className="font-mono-num">30</span> so a 12-month anniversary email
              only reaches clients who are still active. Applies to both trigger types.
            </p>
          </div>

          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Appointment types ({selectedIds.size} selected)
            </Label>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 max-h-52 overflow-y-auto rounded-md border border-zinc-200 p-2">
              {types.length === 0 ? (
                <div className="text-sm text-zinc-500 py-2 text-center">No appointment types available.</div>
              ) : types.map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-50 px-2 py-1 rounded">
                  <Checkbox
                    checked={selectedIds.has(Number(t.id))}
                    onCheckedChange={() => toggleType(t.id)}
                    data-testid={`rule-type-${t.id}`}
                  />
                  <span className="truncate">{t.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-zinc-200">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={form.send_email !== false} onCheckedChange={(v) => set({ send_email: v })} data-testid="rule-send-email" />
              <span className="font-medium">Send email</span>
              <span className="text-2xs text-zinc-500">(turn off for SMS-only rules)</span>
            </label>
          </div>

          {form.send_email !== false && (
            <>
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Subject</Label>
                <Input value={form.subject} onChange={(e) => set({ subject: e.target.value })}
                  className="border-zinc-300 mt-1.5 font-mono-num" data-testid="rule-subject" />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-2xs uppercase tracking-wider text-zinc-500">HTML body</Label>
                  <button type="button" onClick={() => setShowPreview(!showPreview)}
                    className="text-2xs text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1">
                    <Eye className="h-3 w-3" /> {showPreview ? "Hide preview" : "Show preview"}
                  </button>
                </div>
                <Textarea rows={8} value={form.html_body} onChange={(e) => set({ html_body: e.target.value })}
                  className="border-zinc-300 mt-1.5 font-mono-num text-xs" data-testid="rule-body" />
                <div className="mt-1 flex flex-wrap gap-1 text-2xs">
                  <span className="text-zinc-500">Merge:</span>
                  {MERGE_HINTS.map((h) => (
                    <button key={h} type="button"
                      onClick={() => set({ html_body: (form.html_body || "") + h })}
                      className="font-mono-num text-zinc-700 hover:text-zinc-950 hover:underline">{h}</button>
                  ))}
                </div>
                {showPreview && (
                  <div className="mt-3 p-3 rounded-md border border-zinc-200 bg-zinc-50">
                    <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-2">Rendered preview</div>
                    <div className="bg-white p-4 rounded border border-zinc-200 text-sm"
                      /* eslint-disable-next-line react/no-danger */
                      dangerouslySetInnerHTML={{ __html: form.html_body || "<p style='color:#a1a1aa'>(empty)</p>" }} />
                  </div>
                )}
              </div>

              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Sender email (optional)</Label>
                <Input value={form.sender_email || ""} onChange={(e) => set({ sender_email: e.target.value })}
                  placeholder="Uses Resend default"
                  className="border-zinc-300 mt-1.5 font-mono-num max-w-md" data-testid="rule-sender" />
              </div>
            </>
          )}

          {/* SMS companion */}
          <div className="pt-4 border-t border-zinc-200">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={!!form.send_sms} onCheckedChange={(v) => set({ send_sms: v })} data-testid="rule-send-sms" />
              <span className="font-medium">Send SMS</span>
              <span className="text-2xs text-zinc-500">
                {form.send_email !== false
                  ? "(fires alongside the email; skipped if the client has no phone)"
                  : "(this is an SMS-only rule)"}
              </span>
            </label>
            {form.send_sms && (
              <div className="mt-3">
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">SMS body</Label>
                <Textarea rows={3} value={form.sms_body || ""} onChange={(e) => set({ sms_body: e.target.value })}
                  placeholder="Hi {first_name}, thanks for coming in on {anchor_date}!"
                  className="border-zinc-300 mt-1.5 font-mono-num text-sm" data-testid="rule-sms-body" />
                <div className="mt-1 flex flex-wrap items-center gap-1 text-2xs">
                  <span className="text-zinc-500">Merge:</span>
                  {MERGE_HINTS.map((h) => (
                    <button key={h} type="button"
                      onClick={() => set({ sms_body: (form.sms_body || "") + h })}
                      className="font-mono-num text-zinc-700 hover:text-zinc-950 hover:underline">{h}</button>
                  ))}
                  <span className="ml-auto font-mono-num text-zinc-500">
                    {(form.sms_body || "").length} chars · ~{Math.max(1, Math.ceil((form.sms_body || "").length / 160))} SMS part(s)
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rule-cancel">Cancel</Button>
          <Button onClick={save} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="rule-save">
            <Save className="h-4 w-4 mr-1.5" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
